/**
 * Turning a payload into the list of questions one card asks.
 *
 * Kept out of the components because both faces need it and because it is the
 * thing that decides whether a card has eight fields or seven — a verb missing
 * a principal part asks one question fewer rather than asking an unanswerable
 * one.
 */
import {
  KONJUGATION_FIELDS,
  KONJUGATION_LABELS,
  type KonjugationField,
  type KonjugationPayload,
} from '@/core/types';

export type CardField = {
  key: KonjugationField;
  /** What the row is labelled: "ich", "Präteritum". */
  label: string;
  /** Person qualifier for the principal parts, which are asked in the 3rd. */
  sub: string | null;
  answer: string;
  /** 'praesens' rows are the six persons; 'stamm' are the principal parts. */
  group: 'praesens' | 'stamm';
};

/**
 * The questions for one card, in order.
 *
 * The six persons come from `payload.praesens` positionally — the array is
 * built in `KONJUGATION_FIELDS` order at seed time and the two must not drift,
 * which is what the length assertion below is for.
 */
export function fieldsFor(payload: KonjugationPayload): CardField[] {
  const fields: CardField[] = [];

  if (payload.impersonal) {
    // `es regnet` and nothing else. The other five forms are well-formed and
    // unsayable, so the card does not ask for them — and the row is labelled
    // `es` rather than `er/sie/es`, because those are not options here.
    const answer = payload.praesens[2];
    if (answer) {
      fields.push({ key: 'er', label: 'es', sub: null, answer, group: 'praesens' });
    }
  } else {
    KONJUGATION_FIELDS.slice(0, 6).forEach((key, i) => {
      const answer = payload.praesens[i];
      if (!answer) return;
      fields.push({ key, label: KONJUGATION_LABELS[key], sub: null, answer, group: 'praesens' });
    });
  }

  // Asked in the third person singular, which is how the Wortlisten print them
  // and therefore how they are stored. Without the qualifier "Perfekt" is
  // ambiguous between `habe` and `hat`.
  const person = payload.impersonal ? 'es' : 'er/sie/es';
  if (payload.praeteritum) {
    fields.push({
      key: 'praeteritum',
      label: KONJUGATION_LABELS.praeteritum,
      sub: person,
      answer: payload.praeteritum,
      group: 'stamm',
    });
  }
  if (payload.perfekt) {
    fields.push({
      key: 'perfekt',
      label: KONJUGATION_LABELS.perfekt,
      sub: person,
      answer: payload.perfekt,
      group: 'stamm',
    });
  }

  return fields;
}

/**
 * The four principal parts as a dictionary line: infinitive, 3sg, Präteritum,
 * Perfekt. Shown on the card back, where it is the answer key rather than a
 * hint — on the front it would give away the Präsens, because a weak
 * participle contains the third person by construction (`ge`+`arbeitet`).
 */
export function stammformen(de: string, payload: KonjugationPayload): string[] {
  return [de, payload.praesens[2], payload.praeteritum, payload.perfekt].filter(
    (part): part is string => Boolean(part),
  );
}
